import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { ModalService } from '@services/modal/modal.service';
import { BeneficioModal, ModalBeneficioReforma } from '@shared/common/interfaces/modal-beneficios-reforma-tramite-pension.interface';
import { ROUTING_TRAMITE_PENSION } from '@constants/url.constant';
import { FEMALE, TP_MODAL_ACTIVO, TP_PERSONAL_DATA } from '@constants/tramite-pension-constant';
import { StorageService } from '@services/storage/storage.service';
import { BASIC_CLIENT_INFO } from '@constants/data-service.constant';
import { Util } from '@shared/common/utils/util';
import { FiltroSexosBeneficio } from '@constants/reforma-pensiones.constants';

/** Edad objetivo de la Compensación por expectativa de vida de la mujer. */
const EDAD_COMPENSACION_EXPECTATIVA_VIDA = 65;

@Component({
  selector: 'app-modal-reforma-tramite-pension',
  templateUrl: './modal-reforma-tramite-pension.component.html',
  styleUrl: './modal-reforma-tramite-pension.component.scss',
  standalone: false
})
export class ModalReformaTramitePensionComponent implements OnInit {
  @Input() modalBeneficiosReforma: ModalBeneficioReforma;
  // Output para manejar el evento de cierre (llamada al componente padre)
  @Output() closeModal = new EventEmitter<void>();
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  // Data que controla el contenido del acordeón
  isScrolled: boolean = false;
  isOpenCard: boolean = false;
  /** Mujeres entre 64 años 6 meses y 65 años, con texto Firebase presente. */
  mostrarAlertaCompensacionExpectativaVida = false;

  constructor(private readonly modalService: ModalService,
    private readonly router: Router,
    private readonly location: Location,
    private readonly storageService: StorageService,
    private readonly util: Util,
  ) { }

  async ngOnInit(): Promise<void> {
    await this.evaluarAlertaCompensacionExpectativaVida();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeButtonCallback();
  }

  get textoAlertaCompensacionExpectativaVida(): string {
    return this.modalBeneficiosReforma?.textosBeneficios?.alertaCompensacionExpectativaVidaMujer?.trim() ?? '';
  }

  closeButtonCallback() {
    this.modalService.closeModal();
  }

  toggleItem(clickedItem: BeneficioModal, event: Event): void {
    // 1. Previene el comportamiento nativo del navegador
    event.preventDefault();

    // Si el elemento clickeado ya está abierto, simplemente lo cerramos y salimos.
    if (clickedItem.abierto) {
      clickedItem.abierto = false;
      this.isOpenCard = this.modalBeneficiosReforma.textosBeneficios.beneficios.some(item => item.abierto)
      return;
    }

    // 2. Itera sobre el array para actualizar el estado
    this.modalBeneficiosReforma.textosBeneficios.beneficios = this.modalBeneficiosReforma.textosBeneficios.beneficios.map(item => {
      if (item.id === clickedItem.id) {
        // Abre el ítem clickeado
        return { ...item, abierto: true };
      } else {
        // Cierra todos los demás ítems
        return { ...item, abierto: false };
      }
    });

    this.isOpenCard = this.modalBeneficiosReforma.textosBeneficios.beneficios.some(item => item.abierto);
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    // Si la posición vertical del scroll es mayor a 0, activa la sombra
    this.isScrolled = target.scrollTop > 0;
  }

  goToBeneficio(item: BeneficioModal) {
    const beneficioUrl = item.url;
    window.open(beneficioUrl, '_blank');
  }

  goToVideoAtencion(event?: Event) {
    if (event) {
      event.preventDefault();
    }
    this.router.navigate(['/video-atencion']);
    this.modalService.closeModal();
  }

  async goToTramitePension() {
    if (this.esRutaHome()) {
      await this.storageService.remove(TP_MODAL_ACTIVO);
    }else {
      await this.storageService.set(TP_MODAL_ACTIVO, true);
      this.router.navigate([`/${ROUTING_TRAMITE_PENSION.BASE_URL}`]);
    }

    this.modalService.closeModal();
  }

  private esRutaHome(): boolean {
      const url = this.location.path();
      if (url !== `/${ROUTING_TRAMITE_PENSION.BASE_URL}`) {
        return false;
      }
      return true;
    }

  private async evaluarAlertaCompensacionExpectativaVida(): Promise<void> {
    if (!this.textoAlertaCompensacionExpectativaVida) {
      this.mostrarAlertaCompensacionExpectativaVida = false;
      return;
    }

    const clientInfo = await this.storageService.get(BASIC_CLIENT_INFO);
    const personalData = await this.storageService.get(TP_PERSONAL_DATA);

    const sexo =
      clientInfo?.gender ??
      personalData?.sexo ??
      personalData?.gender ??
      '';
    const fechaRaw =
      clientInfo?.birthDate ??
      personalData?.fechaNacimiento ??
      personalData?.birthDate ??
      '';

    const fechaNacimiento = this.normalizarFechaNacimiento(fechaRaw);
    if (!this.esMujer(sexo) || !fechaNacimiento) {
      this.mostrarAlertaCompensacionExpectativaVida = false;
      return;
    }

    this.mostrarAlertaCompensacionExpectativaVida =
      this.util.cumpleEdadLegalEnMenosDe6Meses(
        fechaNacimiento,
        EDAD_COMPENSACION_EXPECTATIVA_VIDA
      );
  }

  private esMujer(sexo: string): boolean {
    const normalized = String(sexo ?? '').trim().toUpperCase();
    return (
      normalized === FiltroSexosBeneficio.F ||
      normalized === FEMALE ||
      normalized === 'FEMENINO' ||
      normalized === 'MUJER'
    );
  }

  /** Normaliza a DD-MM-YYYY, formato esperado por Util.cumpleEdadLegalEnMenosDe6Meses. */
  private normalizarFechaNacimiento(fecha: string): string | null {
    if (!fecha || typeof fecha !== 'string') {
      return null;
    }

    const trimmed = fecha.trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
      return trimmed;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      return trimmed.replaceAll('/', '-');
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const [anio, mes, dia] = trimmed.slice(0, 10).split('-');
      return `${dia}-${mes}-${anio}`;
    }

    return null;
  }
}
